import { Component, Input, OnInit, Output, EventEmitter } from '@angular/core';
import { CommonModule } from '@angular/common';
import { ControlValueAccessor, FormControl, NgControl, ReactiveFormsModule, FormsModule } from '@angular/forms';
import { IonicModule } from '@ionic/angular';
import { format } from 'rut.js';
import { INPUT_TYPES } from './outline-input.constants';

@Component({
    selector: 'app-outline-input',
    templateUrl: './outline-input.component.html',
    styleUrls: ['./outline-input.component.scss'],
    standalone: true,
    imports: [
      CommonModule,
      FormsModule,
      ReactiveFormsModule,
      IonicModule
    ]
})
export class OutlineInputComponent implements OnInit, ControlValueAccessor {
  public inputId = `oi-input-${Math.random().toString(36).substring(2, 9)}`;

  @Input()
  set type(value: string) {
    this.originalType = value;
    this.tempType = value;
  }

  @Output() detectChange = new EventEmitter<boolean>();

  get type(): string {
    return this.originalType;
  }

  originalType: string = INPUT_TYPES.TEXT;
  tempType: string = INPUT_TYPES.TEXT;

  @Input() placeholder: any;
  @Input() autocomplete: string = 'off';
  @Input() maxlength: number = 524288;
  @Input() minlength: number = 0;
  @Input() isPassword: boolean = true;
  @Input() isReadOnly: boolean = false;

  // Label
  @Input() label: any;
  @Input() labelStacked: boolean = false;

  // Error Message
  @Input() errorMessage: any;

  @Input()
  set value(value: string | number) {
    this.valueAux =
      value && this.type === INPUT_TYPES.RUT
        ? format(value.toString(), { dots: true })
        : value;
  }

  get value(): string | number {
    return this.valueAux;
  }

  private valueAux: any;

  public get formControl(): FormControl {
    return this.control.control as FormControl;
  }

  public classByState() {
    return {
      'oi-pristine': !this.isDirty,
      'oi-dirty': this.isDirty,
      'oi-touched': this.formControl.touched,
      'oi-untouched': !this.formControl.touched,
      'oi-valid': this.formControl.valid,
      'oi-invalid': !this.formControl.valid && !this.isFocus,
    };
  }

  public showIcon() {
    return (this.isFocus || this.hasValue) && this.originalType === 'password';
  }

  public showError() {
    if (
      this.originalType === INPUT_TYPES.TEXT ||
      this.originalType === INPUT_TYPES.EMAIL
    ) {
      return this.errorMessage && !this.formControl.valid && !this.isFocus;
    }
    if (this.originalType === INPUT_TYPES.RUT) {
      return (
        this.errorMessage &&
        !this.formControl.valid &&
        !this.isFocus &&
        !this.formControl.untouched
      );
    }

    return (
      this.errorMessage &&
      !this.formControl.valid &&
      !this.isFocus &&
      this.hasValue
    );
  }

  public isDisabled: any;
  public isFocus: boolean = false;
  public isDirty: boolean = false;
  private isDummy: boolean = false;
  get hasValue(): boolean {
    if (this.originalType === INPUT_TYPES.TEXT) {
      return !this.value && !this.formControl.untouched;
    }

    if (this.originalType === INPUT_TYPES.EMAIL) {
      return true;
    }

    if (this.originalType === INPUT_TYPES.RUT) {
      return !this.formControl.untouched || this.value != '';
    }

    return this.value != null && this.value != '' && this.value != undefined;
  }

  propagateChange = (_: any) => {};

  constructor(public control: NgControl) {
    if (control) control.valueAccessor = this;
  }

  ngOnInit() {
    this.formControl.markAsUntouched();
  }

  onChange(event: any) {
    this.isDirty = true;
    this.value = event.detail.value;
    this.propagateChange(this.value);
    this.detectChange.emit(true);
  }

  onFocus() {
    this.isFocus = true;
  }

  onBlur() {
    this.formControl.markAsTouched();
    this.isFocus = false;

    if (
      this.originalType === INPUT_TYPES.TEXT ||
      this.originalType === INPUT_TYPES.EMAIL ||
      this.originalType === INPUT_TYPES.RUT
    ) {
      this.labelStacked = true;
    }
  }

  writeValue(value: string | number): void {
    this.value = value;
  }

  registerOnChange(fn: any): void {
    this.propagateChange = fn;
  }

  registerOnTouched(fn: any): void {
    this.isDummy = true;
  }

  setDisabledState(isDisabled: boolean): void {
    this.isDisabled = isDisabled;
  }
}
